import { Spinner } from './feedback';
import { useState } from 'react';
import type { ChangeRequest, ModpackPreview } from '@mcpanel/core';
import { api } from '../api/client';
import { useApp, errorMessage } from '../app-context';
import { usePoll } from '../hooks/usePoll';
import { ModpackPicker, MissingPackMods } from './ModpackPicker';
import { RequestReview } from './RequestReview';

export function ServerModpack({ serverId }: { serverId: string }) {
  const { canOperate, notify } = useApp();
  const current = usePoll(() => api.serverModpack(serverId), 60000, [serverId]);
  const [open, setOpen] = useState(false);
  const [pack, setPack] = useState<ModpackPreview>();
  const [request, setRequest] = useState<ChangeRequest>();
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!pack || pack.missing?.length) return;
    setBusy(true);
    try {
      setRequest(await api.createRequest('change_modpack', { modpackId: pack.id, confirmReplace: true }, serverId));
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };
  const update = async () => {
    const value = current.data;
    if (!value?.identity.projectId || !value.update) return;
    setBusy(true);
    try {
      setPack(
        await api.selectModpack(
          value.identity.projectId,
          value.update.id,
          value.identity.source === 'curseforge' ? 'curseforge' : 'modrinth',
        ),
      );
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card card-body stack">
      <h2 data-search-id="modpack">Modpack</h2>
      {current.error && <p role="alert">{errorMessage(current.error)}</p>}
      {current.data && (
        <p>
          {current.data.identity.name} / {current.data.identity.versionNumber} ({current.data.identity.source})
        </p>
      )}
      {current.data?.conflicts.length ? (
        <div className="alert warn">
          編集された設定を残しました。更新候補は .new ファイルで確認できます。
          <ul>
            {current.data.conflicts.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {canOperate && !request && (
        <>
          <button className="btn" type="button" disabled={busy} onClick={() => setOpen(!open)}>
            {busy && <Spinner />}
            Modpack を導入
          </button>
          {current.data?.update && (
            <button className="btn primary" type="button" disabled={busy} onClick={() => void update()}>
              {busy && <Spinner />}
              Modpack を更新 ({current.data.update.number})
            </button>
          )}
          {open && (
            <ModpackPicker
              onPending={() => setPack(undefined)}
              onSelect={(value) => {
                setPack(value);
              }}
            />
          )}
          {pack?.missing?.length ? (
            <MissingPackMods
              pack={pack}
              busy={busy}
              onUpload={(projectId, file) => {
                setBusy(true);
                return api
                  .uploadModpackJar(pack.id, projectId, file)
                  .then(setPack)
                  .catch((error) => notify(errorMessage(error), true))
                  .finally(() => setBusy(false));
              }}
            />
          ) : null}
          {pack && !pack.missing?.length && (
            <>
              <p>
                {pack.identity.name} {pack.identity.versionNumber} / {pack.software.family} {pack.software.mcVersion}
              </p>
              <p className="small muted">今の Mod と入れ替えます。入れ替える前に、自動でスナップショットを取ります。</p>
              <button className="btn primary" type="button" disabled={busy} onClick={() => void submit()}>
                {busy && <Spinner />}
                導入・更新を依頼
              </button>
            </>
          )}
        </>
      )}
      {request && (
        <RequestReview
          request={request}
          onDecided={() => {
            setRequest(undefined);
            setPack(undefined);
            setOpen(false);
            void current.reload();
          }}
        />
      )}
    </section>
  );
}
